import { asPartialDef } from "../coerce";
import { matchesAny, type NormalizeOpts } from "../normalize";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

/**
 * "Listen and type": plays audio and student transcribes. Tolerant
 * grading against `transcript` + alternatives. Audio itself is a
 * reference (URL/asset) resolved by renderer; grading is text-based.
 */
export interface ListenTypeDef {
  /** Reference to audio (asset id / URL). Resolved by renderer. */
  audioRef: string;
  transcript: string;
  accept?: string[];
  normalize?: NormalizeOpts;
}

export interface ListenTypeResponse {
  text: string;
}

export const listenTypeBlock: BlockPlugin<ListenTypeDef, ListenTypeResponse> = {
  type: "listen-type",
  interaction: "audio-text",
  autoGradable: true,

  validate(def: unknown): ValidationResult<ListenTypeDef> {
    const d = asPartialDef<ListenTypeDef>(def);
    const errors: string[] = [];
    if (!d) {
      return { ok: false, errors: ["listen-type: definición no es un objeto"] };
    }
    if (typeof d.audioRef !== "string" || d.audioRef.trim() === "") {
      errors.push("listen-type: `audioRef` requerido");
    }
    if (typeof d.transcript !== "string" || d.transcript.trim() === "") {
      errors.push("listen-type: `transcript` requerido");
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      def: {
        audioRef: d.audioRef ?? "",
        transcript: d.transcript ?? "",
        accept: d.accept,
        normalize: d.normalize,
      },
    };
  },

  grade(def: ListenTypeDef, response: ListenTypeResponse | undefined): GradeResult {
    const revealed = def.transcript;
    if (!response) {
      return { correct: false, score: 0, maxScore: 1, revealed };
    }
    const accepted = [def.transcript, ...(def.accept ?? [])];
    const ok = matchesAny(response.text, accepted, def.normalize);
    return { correct: ok, score: ok ? 1 : 0, maxScore: 1, revealed };
  },
};
